import { zPaymentTermsInput } from './payment-terms.js';
import { z } from 'zod';
import { zMoney, zPortalCode, zPositiveMoney } from './primitives.js';
import { zPercent } from './access.js';

/** ON-01…ON-11 and AL-01. */

export const zCountryCode = z.string().length(2).regex(/^[A-Z]{2}$/, 'ISO 3166-1 alpha-2, uppercase');

/**
 * ON-02 — one trade reference. The company is required and the rest is how to reach them.
 *
 * The point of a reference is that somebody rings it before approving, so a row with a name
 * and no way to make contact is a row that cannot do its job; but which contact detail they
 * have varies, so requiring a specific one would simply get a fake one typed.
 */
export const zTradeReference = z.object({
  company: z.string().min(2).max(160),
  contactName: z.string().max(120).optional(),
  email: z.string().email().max(254).optional().or(z.literal('')),
  phone: z.string().max(40).optional(),
  relationship: z.string().max(160).optional(),
});

export const zApply = z.object({
  portalCode: zPortalCode,
  businessName: z.string().min(2).max(160),
  /*
   * ON-02 asks for first and last name separately, and that is not cosmetic: a single box
   * cannot be addressed correctly in a letter or a greeting, and guessing where to split it
   * is wrong in exactly the jurisdictions this portal serves.
   *
   * `contactName` stays on the wire and is still written. Rows predating this release have
   * only that, forty call sites read it, and the retailer record copies it at approval — so
   * the parts are kept in step with it rather than replacing it. The service composes it when
   * the parts are sent, so a caller never has to send the same name twice.
   */
  firstName: z.string().min(1).max(60).optional(),
  lastName: z.string().min(1).max(60).optional(),
  contactName: z.string().min(2).max(120).optional(),
  contactEmail: z.string().email().max(254),
  phone: z.string().max(40).optional(),
  countryCode: zCountryCode,
  city: z.string().max(120).optional(),
  /**
   * ON-03 — shape is checked against the country pack's own pattern at the
   * service, not here. This only guarantees something was typed: the format of
   * an ABN is a property of Australia, not of the wire.
   */
  registrationId: z.string().min(3).max(40),
  /** ON-03 — same arrangement as the registration id: the pattern belongs to the country. */
  postalCode: z.string().max(24).optional(),
  /** ON-02. Free text: the useful set differs per market and no requirement enumerates it. */
  businessType: z.string().max(80).optional(),
  tradeReferences: z.array(zTradeReference).max(10).optional(),
  /**
   * Required or refused by the service, not here: whether a portal asks for it is the portal's
   * `buyingGroup` capability, and the wire does not know which portal is which.
   */
  buyingGroup: z.string().trim().min(1).max(120).optional(),
  note: z.string().max(1000).optional(),
}).refine(
  // One of the two naming styles must arrive. Older clients send `contactName`; the form
  // sends the parts. Neither is required alone, so this is where "a name is required" lives.
  (a) => (a.contactName?.trim().length ?? 0) >= 2
      || ((a.firstName?.trim().length ?? 0) >= 1 && (a.lastName?.trim().length ?? 0) >= 1),
  { message: 'A first and last name are required', path: ['firstName'] },
);
export type TradeReference = z.infer<typeof zTradeReference>;

/** Incoterms Miraki actually trades on. Free text here would reach the documents. */
export const zIncoterm = z.enum(['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP']);

/**
 * ON-05 — the account setup Admin completes **before credentials are issued**.
 *
 * What this used to accept — `advancePct`, `creditLimit`, `incoterm` — belonged to
 * the pre-v1.6 model and is retired: no advance under consignment (D33), the credit
 * limit is replaced by the inventory budget (D37), and the Incoterm left with SH-07.
 * They are gone from the wire rather than from the database, because
 * `mki_trading_terms` has them NOT NULL with defaults and 53 call sites still read
 * them. Dropping them here stops A-02 inventing values; dropping the columns is a
 * separate change across modules four developers are currently in.
 *
 * `tier` went the same way: it was sent as the literal 'New' on every approval, no
 * code reads it, and ON-05's real "gold pricing tier" cannot be captured until D58
 * says what 10K/18K means.
 */
export const zApprove = z.object({
  /**
   * The retailer's own inventory budget, as a wholesale value (BG-01).
   *
   * Required only where the **portal declares no default** — which is the whole of
   * ON-05's "the required settings for the portal". It is checked at the service,
   * against that portal's commercial settings, and never against a country: a third
   * portal states its own rule (D46, SC-07).
   */
  inventoryBudget: zPositiveMoney.optional(),
  /** Why this budget. `mki_retailer_budget_changes.reason` is NOT NULL and the first row is a change like any other. */
  budgetReason: z.string().min(3).max(400).optional(),
  /**
   * BG-04 — the account's **budget tolerance band**, where it is to differ from the portal's.
   *
   * Optional, and omitting it is the normal case: the account then takes the portal's figure,
   * which is what A-02 shows beside the box so the person approving can see what they are
   * accepting. Unlike the budget it is never *required* — there is always a figure in force,
   * so an account approved without one is still orderable, which is the whole reason ON-05
   * requires the budget and not this.
   *
   * `'0'` is a real answer here too: this account gets no tolerance. See `zSetBudget`.
   */
  budgetTolerancePct: zPercent.optional(),
  /**
   * SB-01 — the first subscription term, **already paid**.
   *
   * Required where the portal has the `subscriptions` capability and refused where it does not:
   * the fee is paid before the account is set up, so recording it is part of approval rather
   * than something to chase afterwards. `feeReference` is not optional here, unlike on A-41's
   * own record-a-term route, because at approval the money has by definition already arrived.
   *
   * A date, not a timestamp: a subscription year is a calendar fact (D49).
   */
  subscription: z.object({
    termStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a date, YYYY-MM-DD'),
    termEnd: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be a date, YYYY-MM-DD'),
    /*
     * zPositiveMoney, not zMoney. zMoney checks the decimal FORMAT only, so the approval path
     * accepted a zero or negative annual fee while A-41's own zRecordTerm refused exactly that
     * — one field with two definitions, which is the failure this project has now had four
     * times. Found by running mk-field-design §8's checklist over this screen.
     */
    fee: zPositiveMoney,
    feeReference: z.string().trim().min(1).max(120),
  }).refine((v) => v.termEnd > v.termStart, {
    message: 'the renewal date must be after the start date', path: ['termEnd'],
  }).optional(),
  /**
   * FN-11, **D75** — the account's own payment term, where it is to differ from the portal's
   * due rule.
   *
   * Optional, and omitting it is the normal case: the account then takes the portal's rule,
   * which A-02 shows beside the box so the person approving can see what they are accepting.
   * Exactly `budgetTolerancePct` above, for exactly the same reason — and `'0'` is a real
   * answer here too: payable immediately.
   */
  paymentTermsDays: z.number().int().min(0).max(180).optional(),
  accountManager: z.string().max(120).optional(),
  /** Agreed payment stages, set at creation so the retailer's first T&C acceptance covers them. */
  paymentTerms: zPaymentTermsInput.optional(),
});

/** ON-07 — a rejection without a reason is not a decision anyone can review. */
export const zReject = z.object({ reason: z.string().min(3).max(1000) });
export const zRequestInfo = z.object({ note: z.string().min(3).max(1000) });

export const zSetTerms = z.object({
  /**
   * FN-11, **D75** — net days from the invoice, or **null for "no term of its own"**, which
   * hands the account back to the portal's due rule (SC-07).
   *
   * `.nullable()`, not `.optional()`, and the difference is the whole point here: A-04 replaces
   * this row WHOLESALE, so an omitted field would be indistinguishable from "leave it as it is"
   * on a write that has no "as it is". Null is a stated act — *this account takes the portal's
   * rule* — and it has to be sendable.
   *
   * `0` stays a legal value and is a different statement: payable immediately.
   */
  paymentTermsDays: z.number().int().min(0).max(180).nullable(),
  advancePct: zPercent,
  creditLimit: zMoney,
  incoterm: zIncoterm,
  accountManager: z.string().max(120).nullable().optional(),
  tier: z.string().max(40).nullable().optional(),
});

export const zSetRetailerStatus = z.object({
  status: z.enum(['ACTIVE', 'ON_HOLD', 'DORMANT']),
  reason: z.string().max(1000).optional(),
});

/** AL-01 — closure is irreversible from the portal, so the reason is required. */
/* zCloseRetailer retired with POST /retailers/:id/close — A-27 owns closure, and its
   reason is validated by the lifecycle route's own schema. */

export const zCheckRegistration = z.object({
  countryCode: zCountryCode,
  registrationId: z.string().min(1).max(40),
});

export type Apply = z.infer<typeof zApply>;
export type SetTerms = z.infer<typeof zSetTerms>;


/**
 * ON-08 — an address on a retailer record.
 *
 * `line1` and `city` are the only required parts. A postal code is absent from plenty of
 * jurisdictions, and a required field that cannot be filled honestly is a field that gets
 * filled with a full stop; the country pack validates it where one exists (ON-03).
 *
 * `kind` is accepted on update as well as create, and the service refuses a CHANGE of kind.
 * That is deliberate rather than redundant: a billing address edited into a shipping one is
 * two decisions wearing the coat of one, and the audit row would read as a tidy-up.
 */
export const zRetailerAddress = z.object({
  kind: z.enum(['REGISTERED', 'BILLING', 'SHIPPING']),
  line1: z.string().min(1).max(200),
  line2: z.string().max(200).nullish(),
  city: z.string().min(1).max(120),
  postalCode: z.string().max(24).nullish(),
  countryCode: z.string().length(2).nullish(),
  /** Shipping only in practice: whoever receives a consignment rarely signs the invoice. */
  contactName: z.string().max(120).nullish(),
  contactPhone: z.string().max(40).nullish(),
  isDefault: z.boolean().optional(),
});
export type RetailerAddressInput = z.infer<typeof zRetailerAddress>;


/**
 * ON-08 — the company details Admin maintains on an account.
 *
 * `reason` is REQUIRED, and that is the requirement rather than a flourish: ON-08 says "Every
 * change is audited with a reason". A legal name or a registration number changing is the
 * kind of fact somebody queries months later, and "changed by K. Mercer" without why is an
 * audit row that raises the question instead of answering it.
 *
 * `taxRegistration` is here and deserves a warning on the screen: it decides the tax
 * treatment of every invoice from here on, so it is the one field on this form that changes
 * what the system does rather than what it displays.
 *
 * Contacts are the BUSINESS contact, not a login. A login is somebody who signs in; a contact
 * is who Miraki rings, and they are routinely different people.
 */
export const zUpdateRetailerCompany = z.object({
  name: z.string().min(2).max(160),
  city: z.string().max(120).nullish(),
  taxRegistration: z.string().max(40).nullish(),
  tier: z.string().max(40).nullish(),
  contactName: z.string().max(120).nullish(),
  contactEmail: z.string().email().max(254).nullish().or(z.literal('')),
  contactPhone: z.string().max(40).nullish(),
  reason: z.string().min(3).max(500),
});
export type UpdateRetailerCompany = z.infer<typeof zUpdateRetailerCompany>;

/**
 * CR-57 / D105 — where a retailer account stands on KYC.
 *
 * D105 (03-Nov-2026) supersedes D94: KYC no longer gates the ACCOUNT's approval, it gates the ORDER.
 * A retailer gets an account, can browse and fill a cart, uploads their own KYC after signing in, and
 * can only submit a PO once Admin has approved it. So this is a property of the retailer, not of the
 * application, and it is declared ONCE here and imported by the drizzle schema for its
 * `text(..., { enum })` — the same pattern `consignmentInvoiceStates` uses — so the database column,
 * the API and the screens cannot name different states.
 *
 *   PENDING   nothing approved yet: no pack, or a pack awaiting review. Blocks submitting an order.
 *   APPROVED  Admin has accepted the pack.
 *   REJECTED  Admin declined it, with a reason the retailer sees; they can upload again.
 *
 * **The default for a NEW account is PENDING, and every account that existed before this change is
 * backfilled to APPROVED in the same migration** (0056) — each was approved with documents because
 * D94 required them, and a PENDING default with no backfill would block every live account at
 * checkout the moment it deployed.
 */
export const kycStatuses = ['PENDING', 'APPROVED', 'REJECTED'] as const;
export type KycStatus = (typeof kycStatuses)[number];

/**
 * CR-57 P3 / D105 — Admin rejects a retailer's KYC pack. **A reason is required**, and it is what the
 * retailer reads on R-18: a retailer who does not know which file was wrong uploads the same one
 * again. Trimmed before the length check, so a reason of three spaces is not a reason. Approving
 * takes no body — there is nothing to say, and who and when are the actor's, never the client's.
 */
export const zRejectKyc = z.object({ reason: z.string().trim().min(3).max(1000) });
export type RejectKyc = z.infer<typeof zRejectKyc>;
